import type Stripe from 'stripe';
import { msInADay, trueUpDurationInDays } from '../consts';
import { PlanState } from '../types/plan-state.type';
import { Product } from '../types/product.type';
import { getMetadataField } from './get-metadata-field';

export const isFreeSubscription = (sub: Stripe.Subscription) => {
  if (!sub) return;
  return !sub.id;
};

export const subscriptionWillRenew = (sub: Stripe.Subscription) => {
  if (!sub) return;
  return !sub.cancel_at_period_end;
};

export const findSubscriptionLineItem = (
  items: Stripe.SubscriptionItem[],
  type: 'main' | 'seat_addon' | 'storage_addon' | 'credit_addon'
): Stripe.SubscriptionItem => {
  if (!items) return;
  const item = items.find(item => {
    const itemType = (item.price.product as Stripe.Product).metadata.picflow_type;
    if (itemType === 'enterprise') return type === 'main'; // Enterprise is a main plan
    return itemType === type;
  });
  if (!item) {
    if (type !== 'main') return;
    console.error('Could not find price of type ' + type + ' in subscription items');
    return items[0];
  }
  return item;
};

export const isEnterprise = (mainPrice: Stripe.Price & { product: Stripe.Product }) => {
  if (!mainPrice) return false;
  return mainPrice.product.metadata.picflow_type === 'enterprise';
};

export const calcIncludedSeatCount = (mainPrice: Stripe.Price & { product: Stripe.Product }) => {
  if (!mainPrice) return;
  return Number(getMetadataField(mainPrice, 'included_seat_count'));
};

export const mainProductNameShort = (mainPrice: Stripe.Price & { product: Stripe.Product }) => {
  if (!mainPrice) return 'free';
  const productName = mainPrice.product.name;
  if (!productName.includes('Picflow ')) return productName.toLowerCase();
  return productName.split('Picflow ')[1].toLowerCase();
};

export const calcState = (
  mainPrice: Stripe.Price & { product: Stripe.Product },
  sub: Stripe.Subscription,
  temporarilyHideUpgradeCta: boolean
): PlanState => {
  if (!mainPrice) return 'free';
  if (isEnterprise(mainPrice)) return 'active';
  if (mainPrice.unit_amount === 0) return 'free';
  if (sub.status === 'past_due' && !temporarilyHideUpgradeCta) return 'past-due';
  const willRenew = subscriptionWillRenew(sub);
  if (sub.status === 'trialing') {
    if (willRenew) return 'trialing-will-renew';
    return 'trialing-wont-renew';
  }
  if (willRenew) return 'active';
  return 'pending-termination';
};

export const isCurrentProduct = (
  product: Product,
  mainPrice: Stripe.Price & { product: Stripe.Product },
  sub: Stripe.Subscription
) => {
  if (!sub) return false;
  if (product.isFree && isFreeSubscription(sub)) return true;
  if (product.isEnterprise && isEnterprise(mainPrice)) return true;
  const stateProductId = mainPrice?.product.id;
  if (stateProductId !== product.id) return false;
  if (!product.prices.some(price => price.id === mainPrice.id)) return false;
  return true;
};

export const isCurrentPrice = (
  product: Product,
  mainPrice: Stripe.Price & { product: Stripe.Product },
  sub: Stripe.Subscription,
  billingFrequency: Stripe.Price.Recurring.Interval
) => {
  if (!mainPrice) return false;
  if (!isCurrentProduct(product, mainPrice, sub)) return false;
  return mainPrice.recurring.interval === billingFrequency;
};

/**
 * Returns the next true up period end date based on the billing period end date
 */
export const getNextTrueUpPeriodEndDate = (billingPeriodEndMs: number): number => {
  if (!billingPeriodEndMs) return;
  const billingPeriodEndDate = new Date(billingPeriodEndMs);
  // removing three months from the billing period end date
  const monthsBetweenTrueUps = 3;
  const threeMonthsBefore = new Date(billingPeriodEndDate);
  threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - monthsBetweenTrueUps);
  // if the date is in the past, we return the billing period end date
  const dateIsInThePast = threeMonthsBefore.getTime() < Date.now();
  if (dateIsInThePast) return billingPeriodEndMs;
  // otherwise we recursively call this function with the new date
  return getNextTrueUpPeriodEndDate(threeMonthsBefore.getTime());
};

export const isInTrueUp = (billingPeriodEndMs: number) => {
  const daysRemainingUntilTrueUpEnd = (billingPeriodEndMs - Date.now()) / msInADay;
  return daysRemainingUntilTrueUpEnd < trueUpDurationInDays;
};
